import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { type ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Channel } from '../../../api';

import { ChannelPicker } from './ChannelPicker';
import { EmptyChannels } from './EmptyChannels';
import { ReasonText } from './ReasonText';
import { filterChannels, selectedCount } from './channel-list';
import type { ChannelsView } from './channels-view';
import type { ScreenFailure } from './screen-failure';

// MUI's Alert is role="alert" by default; a removal is a status, not an error (S5 R5). A constant, not a text.
const STATUS_ROLE = 'status';

/** Props of {@link ChannelSelectionScreen}. */
export interface ChannelSelectionScreenProps {
  readonly view: ChannelsView;
  readonly busy: ReadonlySet<string>;
  /** The last refused tick or untick (R23), or `null`. */
  readonly writeFailure: ScreenFailure | null;
  /** Name of the channel just removed (R5), or `null`. */
  readonly removed: string | null;
  readonly onToggle: (channel: Channel) => void;
  readonly onReload: () => void;
  readonly onJoin: (url: string) => void;
  readonly onSignInAgain: () => void;
  readonly onContinue: () => void;
}

/**
 * Screen E3 · Channel selection (specs/S5-channel-selection/spec.md; US-04; mockup 4).
 * Presentational: no request, no navigation. It owns its `main` landmark because it renders
 * outside `AppLayout` (R15).
 */
export function ChannelSelectionScreen(props: ChannelSelectionScreenProps): ReactElement {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const { view } = props;
  const channels = view.kind === 'list' ? view.channels : [];
  const shown = filterChannels(channels, query);
  const count = selectedCount(channels);
  return (
    <Box
      component="main"
      sx={{
        minHeight: '100dvh',
        width: '100%',
        maxWidth: 560,
        mx: 'auto',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        px: 2,
        pt: 3,
        pb: 3.5,
      }}
    >
      <Typography sx={{ fontSize: 13, color: 'text.secondary', fontWeight: 600 }}>
        {t('onboarding.channels.step')}
      </Typography>
      <Typography variant="h1">{t('onboarding.channels.title')}</Typography>

      {view.kind === 'loading' ? (
        <Stack spacing={1} aria-busy="true" aria-label={t('onboarding.channels.loading')}>
          {[0, 1, 2, 3].map((n) => (
            <Skeleton key={n} variant="rounded" height={56} />
          ))}
        </Stack>
      ) : null}

      {view.kind === 'failed' ? (
        <Stack spacing={1.5}>
          <ReasonText failure={view.failure} />
          {view.failure.reasonCode === 'TELEGRAM_SESSION_LOST' ? (
            <Button variant="contained" onClick={props.onSignInAgain}>
              {t('onboarding.channels.signInAgain')}
            </Button>
          ) : (
            <Button variant="outlined" onClick={props.onReload}>
              {t('onboarding.channels.retry')}
            </Button>
          )}
        </Stack>
      ) : null}

      {view.kind === 'list' && channels.length === 0 ? (
        <EmptyChannels
          affiliateChannelUrl={view.affiliateChannelUrl}
          onJoin={props.onJoin}
          onReload={props.onReload}
        />
      ) : null}

      {view.kind === 'list' && channels.length > 0 ? (
        <>
          <TextField
            type="search"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
            }}
            placeholder={t('onboarding.channels.searchPlaceholder', { count: channels.length })}
            slotProps={{ htmlInput: { 'aria-label': t('onboarding.channels.searchLabel') } }}
          />
          {shown.length === 0 ? (
            <Typography>{t('onboarding.channels.noResult', { query: query.trim() })}</Typography>
          ) : (
            <ChannelPicker channels={shown} busy={props.busy} onToggle={props.onToggle} />
          )}
          {props.writeFailure === null ? null : <ReasonText failure={props.writeFailure} />}
          {props.removed === null ? null : (
            <Alert severity="info" role={STATUS_ROLE}>
              {t('onboarding.channels.removed', { name: props.removed })}
            </Alert>
          )}
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            {t('onboarding.channels.removalNote')}
          </Typography>
        </>
      ) : null}

      {view.kind === 'list' ? (
        <Box
          sx={{
            mt: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>
            {count === 0
              ? t('onboarding.channels.selectedNone')
              : t('onboarding.channels.selected', { count })}
          </Typography>
          <Button variant="contained" disabled={count === 0} onClick={props.onContinue}>
            {t('onboarding.channels.continue')}
          </Button>
        </Box>
      ) : null}
    </Box>
  );
}
